import logger from "@src/utils/logger";
import type { FC, ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const TRANSITION_TIMEOUT_MS = 10_000;

export interface InteractionGuardContextValue {
  isActionPending: (key?: string) => boolean;
  isUiBlocked: boolean;
  /**
   * Synchronous, ref-backed check for "is anything guarded in flight right now".
   * Unlike `isUiBlocked` (React state, only current as of the last render), this
   * reads the same refs `runExclusive` and `beginTransition`/`endTransition` write
   * to, so it is safe to call from event handlers — e.g. `beforeunload` — that run
   * outside React's render cycle and cannot wait for an effect to flush.
   */
  isBlockedNow: () => boolean;
  suppressGlobalOverlay: boolean;
  setSuppressGlobalOverlay: (suppressed: boolean) => void;
  runExclusive: <T>(key: string, action: () => Promise<T>) => Promise<T | undefined>;
  beginTransition: (to: string) => void;
  endTransition: () => void;
}

const InteractionGuardContext = createContext<InteractionGuardContextValue | null>(null);

export const InteractionGuardProvider: FC<{ children: ReactNode }> = ({ children }) => {
  // Ref-backed set for immediate same-tick locking. React state is NOT the source of truth
  // for the lock — the ref is checked synchronously inside runExclusive before any re-render.
  const pendingKeysRef = useRef<Set<string>>(new Set());
  // Mirror of pendingKeysRef into React state solely for rendering isActionPending / isUiBlocked.
  const [pendingKeysSnapshot, setPendingKeysSnapshot] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [isTransitionPending, setIsTransitionPending] = useState(false);
  // Ref-backed mirror of isTransitionPending for the same reason pendingKeysRef
  // backs pendingKeysSnapshot: isBlockedNow() must read a value that is current
  // synchronously, not one that lags behind by a render + effect cycle.
  const isTransitionPendingRef = useRef(false);
  const [suppressGlobalOverlay, setSuppressGlobalOverlay] = useState(false);
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const transitionTargetRef = useRef<string | null>(null);

  const isActionPending = useCallback((key?: string): boolean => {
    if (key !== undefined) return pendingKeysRef.current.has(key);
    return pendingKeysRef.current.size > 0;
  }, []);

  const isBlockedNow = useCallback((): boolean => {
    return pendingKeysRef.current.size > 0 || isTransitionPendingRef.current;
  }, []);

  const isUiBlocked = pendingKeysSnapshot.size > 0 || isTransitionPending;

  const runExclusive = useCallback(
    async <T,>(key: string, action: () => Promise<T>): Promise<T | undefined> => {
      if (pendingKeysRef.current.has(key)) {
        logger.warn({
          text: "InteractionGuard: duplicate action ignored",
          value: { key },
          location: "InteractionGuardContext.runExclusive",
        });
        return undefined;
      }

      pendingKeysRef.current.add(key);
      setPendingKeysSnapshot(new Set(pendingKeysRef.current));

      try {
        return await action();
      } finally {
        pendingKeysRef.current.delete(key);
        setPendingKeysSnapshot(new Set(pendingKeysRef.current));
      }
    },
    [],
  );

  const beginTransition = useCallback((to: string) => {
    transitionTargetRef.current = to;
    isTransitionPendingRef.current = true;
    setIsTransitionPending(true);

    if (transitionTimeoutRef.current !== null) {
      clearTimeout(transitionTimeoutRef.current);
    }

    transitionTimeoutRef.current = setTimeout(() => {
      transitionTimeoutRef.current = null;
      isTransitionPendingRef.current = false;
      setIsTransitionPending(false);
      logger.warn({
        text: "InteractionGuard: transition lock timed out, clearing automatically",
        value: { to: transitionTargetRef.current },
        location: "InteractionGuardContext.beginTransition",
      });
    }, TRANSITION_TIMEOUT_MS);
  }, []);

  const endTransition = useCallback(() => {
    if (transitionTimeoutRef.current !== null) {
      clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = null;
    }
    transitionTargetRef.current = null;
    isTransitionPendingRef.current = false;
    setIsTransitionPending(false);
  }, []);

  // Clear the transition timeout on unmount to prevent setState calls after teardown.
  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current !== null) {
        clearTimeout(transitionTimeoutRef.current);
        transitionTimeoutRef.current = null;
      }
    };
  }, []);

  const value = useMemo<InteractionGuardContextValue>(
    () => ({
      isActionPending,
      isUiBlocked,
      isBlockedNow,
      suppressGlobalOverlay,
      setSuppressGlobalOverlay,
      runExclusive,
      beginTransition,
      endTransition,
    }),
    [
      isActionPending,
      isUiBlocked,
      isBlockedNow,
      suppressGlobalOverlay,
      runExclusive,
      beginTransition,
      endTransition,
    ],
  );

  return (
    <InteractionGuardContext.Provider value={value}>{children}</InteractionGuardContext.Provider>
  );
};

/**
 * Returns the InteractionGuard context value.
 * Returns a safe no-op fallback when called outside InteractionGuardProvider so that
 * hooks such as useNavigateWithUTM remain usable in contexts where the provider is absent.
 */
export const useInteractionGuard = (): InteractionGuardContextValue => {
  const ctx = useContext(InteractionGuardContext);
  if (!ctx) {
    return {
      isActionPending: () => false,
      isUiBlocked: false,
      isBlockedNow: () => false,
      suppressGlobalOverlay: false,
      setSuppressGlobalOverlay: () => {},
      runExclusive: (_key, action) => action(),
      beginTransition: () => {},
      endTransition: () => {},
    };
  }
  return ctx;
};
